import { defineStore } from 'pinia'

interface PermissionState {
	permissions: Permission[]
	categories: Map<string, PermissionCategory>
	userPermissionIds: Set<string>
	userPermissionNames: Set<PermissionValue>
	loading: boolean
	error: string | null
}

export const usePermissionStore = defineStore('permissions', {
	state: (): PermissionState => ({
		permissions: [],
		categories: new Map(),
		userPermissionIds: new Set(),
		userPermissionNames: new Set(),
		loading: false,
		error: null
	}),

	getters: {
		/**
		 * Get all permissions grouped by category
		 */
		permissionsByCategory(): Map<string, Permission[]> {
			const grouped = new Map<string, Permission[]>()

			this.permissions.forEach(permission => {
				const categoryId = permission.permission_category_id
				if (!grouped.has(categoryId)) {
					grouped.set(categoryId, [])
				}
				grouped.get(categoryId)!.push(permission)
			})

			return grouped
		},

		/**
		 * Get user's permissions with metadata
		 */
		userPermissions(): Permission[] {
			return this.permissions.filter(p => this.userPermissionIds.has(p.id))
		},

		/**
		 * Get permissions by category name
		 */
		getPermissionsByCategory: state => (categoryName: string) => {
			return state.permissions.filter(p => p.permission_category.name === categoryName)
		},

		/**
		 * Check if user has permission by ID
		 */
		hasPermission:
			state =>
			(permissionId: string): boolean => {
				return state.userPermissionIds.has(permissionId)
			},

		/**
		 * Check if user has permission by NAME
		 */
		hasPermissionByName:
			state =>
			(permissionName: PermissionValue): boolean => {
				return state.userPermissionNames.has(permissionName)
			},

		/**
		 * Check if user has ANY of the provided permissions
		 */
		hasAnyPermission:
			state =>
			(permissionNames: readonly PermissionValue[]): boolean => {
				return Array.from(permissionNames).some(name => state.userPermissionNames.has(name))
			},

		/**
		 * Check if user has ALL of the provided permissions
		 */
		hasAllPermissions:
			state =>
			(permissionNames: readonly PermissionValue[]): boolean => {
				return Array.from(permissionNames).every(name => state.userPermissionNames.has(name))
			}
	},

	actions: {
		async fetchPermissions(user_id: string) {
			this.loading = true
			this.error = null
			const { getItem, storeItem } = useDraft<string, Permission[]>('permissions')
			const encryptedData = decryptString<Permission[]>(getItem.value)

			try {
				if (!user_id) {
					if (Array.isArray(encryptedData)) {
						this.setPermissions(encryptedData)
					}
					return
				}
				const { data } = await useAPI<IRequestResponse<Permission[]>>(
					`${API_ENDPOINTS.getAccountPermission}${user_id}`
				)
				const response = data.value?.data ?? []
				this.setPermissions(response)
				storeItem(response, true)
			} catch (err) {
				this.error = err instanceof Error ? err.message : 'Failed to load permissions'
				console.error('Permission fetch error:', err)
			} finally {
				this.loading = false
			}
		},

		setPermissions(permissions: Permission[]) {
			this.permissions = permissions
			const categoryMap = new Map<string, PermissionCategory>()
			permissions.forEach(p => {
				if (!categoryMap.has(p.permission_category_id)) {
					categoryMap.set(p.permission_category_id, p.permission_category)
				}
			})
			this.categories = categoryMap
			this.setUserPermissionsByNames(permissions.map(p => p.name as PermissionValue))
			this.setUserPermissions(permissions.map(p => p.id))
		},

		/**
		 * Set user's assigned permissions (by IDs)
		 */
		setUserPermissions(permissionIds: string[]) {
			this.userPermissionIds = new Set(permissionIds)
			const nameSet = new Set<PermissionValue>()
			permissionIds.forEach(id => {
				const perm = this.permissions.find(p => p.id === id)
				if (perm) {
					nameSet.add(perm.name as PermissionValue)
				}
			})
			this.userPermissionNames = nameSet
		},

		/**
		 * Set user's assigned permissions (by names)
		 */
		setUserPermissionsByNames(permissionNames: PermissionValue[]) {
			this.userPermissionNames = new Set(permissionNames)
			const idSet = new Set<string>()
			permissionNames.forEach(name => {
				const perm = this.permissions.find(p => p.name === name)
				if (perm) {
					idSet.add(perm.id)
				}
			})
			this.userPermissionIds = idSet
		},

		/**
		 * Add a permission to user (by name)
		 */
		addUserPermission(permissionName: PermissionValue) {
			this.userPermissionNames.add(permissionName)
			const perm = this.permissions.find(p => p.name === permissionName)
			if (perm) {
				this.userPermissionIds.add(perm.id)
			}
		},

		/**
		 * Remove a permission from user (by name)
		 */
		removeUserPermission(permissionName: PermissionValue) {
			this.userPermissionNames.delete(permissionName)
			const perm = this.permissions.find(p => p.name === permissionName)
			if (perm) {
				this.userPermissionIds.delete(perm.id)
			}
		},

		/**
		 * Clear all permissions (useful on logout)
		 */
		clearPermissions() {
			this.userPermissionIds.clear()
			this.userPermissionNames.clear()
			this.permissions = []
			this.categories.clear()
		}
	}
})
